import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, Share2, Users, DollarSign, TrendingUp, Gift, Trophy, Loader2, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Social share icons
const WhatsAppIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const Affiliate = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_referrals: 0,
    active_referrals: 0,
    pending_referrals: 0,
    total_earnings: 0,
    pending_earnings: 0,
    available_earnings: 0
  });
  const [referrals, setReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      // Fetch referral code
      const codeRes = await api.get('/api/referrals/my-code');
      setReferralCode(codeRes.data.code);
      setReferralLink(codeRes.data.link);

      // Fetch stats
      const statsRes = await api.get('/api/referrals/stats');
      setStats(statsRes.data);

      // Fetch referral list
      const listRes = await api.get('/api/referrals/list?limit=10');
      setReferrals(listRes.data);

      // Fetch leaderboard
      const leaderRes = await api.get('/api/referrals/leaderboard?limit=5');
      setLeaderboard(leaderRes.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      // Fallback to generated code if API fails
      if (user?.id) {
        const code = `PARACH-${user.id.substring(0, 8).toUpperCase()}`;
        setReferralCode(code);
        setReferralLink(`https://parachpay.com/register?ref=${code}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join ParachPay',
          text: `Sign up for ParachPay and get $5 bonus! Use my referral code: ${referralCode}`,
          url: referralLink
        });
      } catch (err) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Join ParachPay and get a $5 welcome bonus! 🎁\n\nUse my referral code: ${referralCode}\n\n${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(`Join @ParachPay and get a $5 welcome bonus! Use my referral code: ${referralCode}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join ParachPay - Get $5 Welcome Bonus!');
    const body = encodeURIComponent(`Hi!\n\nI've been using ParachPay for international payments and I thought you might like it too.\n\nSign up using my referral link and you'll get a $5 welcome bonus:\n${referralLink}\n\nOr use my referral code: ${referralCode}\n\nBest regards`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl" data-testid="affiliate-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Affiliate Program</h1>
        <p className="text-muted-foreground mt-2">
          Invite friends and earn rewards when they join ParachPay
        </p>
      </div>

      {/* Referral Code Card */}
      <Card className="mb-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Your Referral Code
          </CardTitle>
          <CardDescription className="text-emerald-100">
            Share this code with friends to earn rewards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              value={referralCode}
              readOnly
              className="bg-white/20 border-white/30 text-white placeholder:text-white/70 text-lg font-mono"
              data-testid="referral-code-display"
            />
            <Button 
              variant="secondary" 
              size="icon"
              onClick={copyReferralCode}
              data-testid="copy-code-btn"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2 mb-4">
            <Button 
              className="flex-1 bg-white text-emerald-600 hover:bg-emerald-50"
              onClick={copyReferralLink}
              data-testid="copy-link-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              className="flex-1 bg-white text-emerald-600 hover:bg-emerald-50"
              onClick={shareReferral}
              data-testid="share-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
          
          {/* Social Share Buttons */}
          <div className="flex gap-2">
            <Button 
              size="sm"
              className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white"
              onClick={shareViaWhatsApp}
              data-testid="share-whatsapp-btn"
            >
              <WhatsAppIcon />
              <span className="ml-2 hidden sm:inline">WhatsApp</span>
            </Button>
            <Button 
              size="sm"
              className="flex-1 bg-black hover:bg-gray-800 text-white"
              onClick={shareViaTwitter}
              data-testid="share-twitter-btn"
            >
              <TwitterIcon />
              <span className="ml-2 hidden sm:inline">Twitter</span>
            </Button>
            <Button 
              size="sm"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={shareViaEmail}
              data-testid="share-email-btn"
            >
              <Mail className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">Email</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_referrals}</p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_referrals}</p>
                <p className="text-xs text-muted-foreground">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.total_earnings.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Total Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <Gift className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.pending_earnings.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-amber-100 text-amber-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {entry.rank}
                    </span>
                    <span className="font-medium">{entry.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{entry.referrals} referrals</p>
                    <p className="text-xs text-muted-foreground">${entry.earnings.toFixed(2)} earned</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Referrals */}
      {referrals.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referrals.map((ref) => (
                <div key={ref.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{ref.referred_name || ref.referred_email}</p>
                    <p className="text-xs text-muted-foreground">{new Date(ref.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      ref.status === 'rewarded' ? 'bg-green-100 text-green-700' :
                      ref.status === 'active' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {ref.status === 'rewarded' ? 'Completed' : ref.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">${ref.reward_amount.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">
                1
              </div>
              <div>
                <h4 className="font-medium">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">
                  Share your unique referral code or link with friends and family
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">
                2
              </div>
              <div>
                <h4 className="font-medium">They Sign Up</h4>
                <p className="text-sm text-muted-foreground">
                  When they create an account using your code, both of you get rewarded
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">
                3
              </div>
              <div>
                <h4 className="font-medium">Earn Rewards</h4>
                <p className="text-sm text-muted-foreground">
                  Earn up to $10 for each verified referral. They get $5 bonus too!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Affiliate;
